import { useCallback, useMemo } from "react";
import { VehiclePositionInfo } from "../models/vehicle/vehiclePosition";
import { Base } from "../framework/base";
import { useAppSelector } from "../framework/customStore";
import { hasActiveWorkTime } from "../components/monitorView/monitorViewUtils";
import {
    CountByPrimaryFilter,
    EmployeeData,
    MonitorViewFleetSummaryTotals,
    VehicleData,
} from "../models/monitorView/monitorViewTypes";
import { EmployeeListItemDto } from "../models/employee/employee";
import { WorkShiftDtoBasic } from "../models/workShift/workShift";
import { TransportOrderListItemDto } from "../models/transport/transportOrder";

const mapCommonData = (position: VehiclePositionInfo) => ({
    brand: position?.brand,
    carId: position?.carId,
    codriverGmt: position?.codriverGmt,
    codriverId: position?.codriverId,
    coords: { lat: position?.latitude, lng: position?.longitude },
    driverGmt: position?.driverGmt,
    driverId: position?.driverId,
    driving: position?.driving,
    drivingGmt: position?.drivingGmt,
    employeeWorkTimeTypes: position?.employeeWorkTimeTypes,
    latitude: position?.latitude,
    longitude: position?.longitude,
    registerNumber: position?.registerNumber,
    speed: position?.speed,
    timestamp: position?.timestamp,
    vehicleId: position?.vehicleId,
    workTimeTypeName:
        position?.employeeWorkTimeTypes?.[0]?.workTimeTypeName,
    workTimeTypeType:
        position?.employeeWorkTimeTypes?.[0]?.workTimeTypeType,
});

const mapVehicleData = (p: VehiclePositionInfo): VehicleData => ({
    ...mapCommonData(p),
    employeeName: p.employeeWorkTimeTypes?.[0]?.employeeName,
    employeeId: p.employeeWorkTimeTypes?.[0]?.employeeId,
});

interface UseFilteredFleetDataParams {
    positions: VehiclePositionInfo[];
    workShifts: WorkShiftDtoBasic[];
    transportOrders?: TransportOrderListItemDto[];
}

interface UseFilteredFleetDataResult {
    filteredVehicleData: VehicleData[];
    filteredEmployeeData: EmployeeData[];
    countByPrimaryFilter: CountByPrimaryFilter;
    fleetSummaryTotals: MonitorViewFleetSummaryTotals;
    countByWorkTimeType: { [key: string]: number };
    countByDrivingStatus: { [key: number]: number };
    positionByEmployeeId: Map<string, VehiclePositionInfo>;
}

export function useFilteredFleetData({
    positions,
    workShifts,
    transportOrders,
}: UseFilteredFleetDataParams): UseFilteredFleetDataResult {
    const employees = useAppSelector((state) => state.employees.employees);
    const vehicles = useAppSelector((state) => state.monitorView.vehicles);

    const primaryFilter = useAppSelector(
        (state) => state.monitorViewFilters.primaryFilter
    );
    const selectedVehicleGroupIds = useAppSelector(
        (state) => state.monitorViewFilters.selectedVehicleGroupIds
    );
    const selectedWorkTimeTypeIds = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypeIds
    );
    const selectedTelematicsFilters = useAppSelector(
        (state) => state.monitorViewFilters.selectedTelematicsFilters
    );
    const showWorkingWithoutPhase = useAppSelector(
        (state) => state.monitorViewFilters.showWorkingWithoutPhase
    );
    const noWork = useAppSelector(
        (state) => state.monitorViewFilters.noWork
    );
    const sortOrder = useAppSelector(
        (state) => state.monitorViewFilters.sortOrder
    );
    const workTimeTypes = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypes
    );

    // Lookup map: workTimeTypeId → { name, type } for resolving basic DTO fields
    const workTimeTypeById = useMemo(() => {
        const map = new Map<string, { name: string; type: number }>();
        for (const wtt of workTimeTypes ?? []) {
            map.set(wtt.workTimeTypeId, { name: wtt.name, type: wtt.type });
        }
        return map;
    }, [workTimeTypes]);

    // Vehicle filter: apply primary filter + sub-filters
    // Accepts any object with employeeWorkTimeTypes and driving — works for both
    // VehiclePositionInfo and VehicleData without needing a cast.
    const vehiclePrimaryFilter = useCallback(
        (item: {
            employeeWorkTimeTypes?: { workTimeTypeId?: string | null }[];
            driving?: number;
        }) => {
            if (primaryFilter === "all") return true;

            const isWorking = hasActiveWorkTime(item);

            if (primaryFilter === "working") {
                if (!isWorking) return false;
                const workTimeTypeId =
                    item.employeeWorkTimeTypes[0]?.workTimeTypeId;
                if (workTimeTypeId === null || workTimeTypeId === undefined) {
                    return showWorkingWithoutPhase;
                }
                return (
                    !selectedWorkTimeTypeIds ||
                    selectedWorkTimeTypeIds.includes(workTimeTypeId)
                );
            }

            if (primaryFilter === "notWorking") {
                if (isWorking) return false;
                if (selectedTelematicsFilters.length === 0) return true;
                return selectedTelematicsFilters.includes(item.driving);
            }

            return true;
        },
        [
            primaryFilter,
            selectedWorkTimeTypeIds,
            selectedTelematicsFilters,
            showWorkingWithoutPhase,
        ]
    );

    // Employee filter: apply primary filter + sub-filters
    const employeeFilter = useCallback(
        (p: VehiclePositionInfo) => {
            if (primaryFilter === "all") {
                if (
                    !p?.employeeWorkTimeTypes ||
                    p?.employeeWorkTimeTypes.length === 0
                ) {
                    return noWork;
                }
                const hasNoWorkTime =
                    p.employeeWorkTimeTypes[0].workTimeTypeId === null;
                const matchesType =
                    !selectedWorkTimeTypeIds ||
                    p.employeeWorkTimeTypes.some((w) =>
                        selectedWorkTimeTypeIds?.includes(w.workTimeTypeId)
                    );
                return noWork ? hasNoWorkTime || matchesType : matchesType;
            }

            if (primaryFilter === "working") {
                if (
                    !p?.employeeWorkTimeTypes ||
                    p?.employeeWorkTimeTypes.length === 0
                ) {
                    return false;
                }
                const workTimeTypeId =
                    p.employeeWorkTimeTypes[0]?.workTimeTypeId;
                if (workTimeTypeId === null || workTimeTypeId === undefined) {
                    return showWorkingWithoutPhase;
                }
                return (
                    !selectedWorkTimeTypeIds ||
                    selectedWorkTimeTypeIds.includes(workTimeTypeId)
                );
            }

            if (primaryFilter === "notWorking") {
                if (
                    !p?.employeeWorkTimeTypes ||
                    p?.employeeWorkTimeTypes.length === 0
                ) {
                    return true;
                }
                return (
                    p.employeeWorkTimeTypes[0]?.workTimeTypeId === null ||
                    p.employeeWorkTimeTypes[0]?.workTimeTypeId === undefined
                );
            }

            return true;
        },
        [
            primaryFilter,
            noWork,
            selectedWorkTimeTypeIds,
            showWorkingWithoutPhase,
        ]
    );

    // O(1) lookup maps
    const vehicleById = useMemo(() => {
        const map = new Map<string, (typeof vehicles)[number]>();
        for (const v of vehicles ?? []) {
            if (v.id) map.set(v.id.toLowerCase(), v);
        }
        return map;
    }, [vehicles]);

    const employeeById = useMemo(() => {
        const map = new Map<string, EmployeeListItemDto>();
        for (const e of employees ?? []) {
            if (e.id) map.set(e.id.toLowerCase(), e);
        }
        return map;
    }, [employees]);

    const positionByEmployeeId = useMemo(() => {
        const map = new Map<string, VehiclePositionInfo>();
        for (const p of positions ?? []) {
            for (const w of p.employeeWorkTimeTypes ?? []) {
                if (w.employeeId) {
                    map.set(w.employeeId.toLowerCase(), p);
                }
            }
        }
        return map;
    }, [positions]);

    const workShiftsByEmployeeId = useMemo(() => {
        const map = new Map<string, WorkShiftDtoBasic[]>();
        for (const w of workShifts ?? []) {
            if (!w.employeeId) continue;
            const key = w.employeeId.toLowerCase();
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(w);
        }
        return map;
    }, [workShifts]);

    // Count badge computations
    const countByWorkTimeType = useMemo(() => {
        const counts: { [key: string]: number } = {};
        for (const e of employees ?? []) {
            const position = e.id
                ? positionByEmployeeId.get(e.id.toLowerCase())
                : undefined;
            const workTimeTypeId =
                position?.employeeWorkTimeTypes[0]?.workTimeTypeId;
            if (!position || workTimeTypeId === null) {
                counts["noWork"] = (counts["noWork"] || 0) + 1;
            } else if (workTimeTypeId === undefined) {
                counts["workNoPhase"] = (counts["workNoPhase"] || 0) + 1;
            } else {
                counts[workTimeTypeId] = (counts[workTimeTypeId] || 0) + 1;
                counts["work"] = (counts["work"] || 0) + 1;
            }
        }
        return counts;
    }, [positionByEmployeeId, employees]);

    const countByDrivingStatus = useMemo(() => {
        const counts: { [key: number]: number } = {};
        for (const p of positions ?? []) {
            if (!hasActiveWorkTime(p)) {
                const drivingStatus = p.driving;
                counts[drivingStatus] = (counts[drivingStatus] || 0) + 1;
            }
        }
        return counts;
    }, [positions]);

    const mapEmployeeData = useCallback(
        (e: EmployeeListItemDto): EmployeeData => {
            const eid = e.id?.toLowerCase();
            const position = eid ? positionByEmployeeId.get(eid) : undefined;
            const empShifts = eid ? (workShiftsByEmployeeId.get(eid) ?? []) : [];
            return {
                ...mapCommonData(position),
                employeeId: e.id,
                employeeName: e?.fullName,
                salaryGroupId: e?.salaryGroupId,
                workShifts: empShifts,
                currentWorkShift: empShifts
                    .filter((w) => !w.endTime)
                    .sort(
                        (a, b) =>
                            new Date(b.startTime).getTime() -
                            new Date(a.startTime).getTime()
                    )?.[0],
            };
        },
        [positionByEmployeeId, workShiftsByEmployeeId]
    );

    // Step 1: Filter + map + sort positions
    const baseVehicleData = useMemo(() => {
        return (
            positions
                ?.filter(
                    (p) =>
                        selectedVehicleGroupIds.length === 0 ||
                        selectedVehicleGroupIds.includes(
                            vehicleById.get(p.vehicleId?.toLowerCase())
                                ?.vehicleGroupId
                        )
                )
                .map(mapVehicleData)
                .sort((a, b) => {
                    if (sortOrder === "employeeName") {
                        return Base.stringCompare(
                            a.employeeName ?? "",
                            b.employeeName ?? ""
                        );
                    }
                    return Base.stringCompare(
                        a.registerNumber,
                        b.registerNumber
                    );
                }) ?? []
        );
    }, [positions, selectedVehicleGroupIds, sortOrder, vehicleById]);

    // Step 2: Augment with workShift vehicles + transport orders
    const vehicleData: VehicleData[] = useMemo(() => {
        const mapped = [...baseVehicleData];

        const mappedVehicleIds = new Set(
            mapped.map((v) => v.vehicleId?.toLowerCase())
        );

        // Add vehicles that have work shift time slots but no GPS position
        for (const shift of workShifts ?? []) {
            for (const slot of shift.workShiftTimeSlots ?? []) {
                if (
                    !slot.vehicleId ||
                    mappedVehicleIds.has(slot.vehicleId.toLowerCase())
                )
                    continue;

                const vehicleInfo = vehicleById.get(slot.vehicleId.toLowerCase());
                if (
                    !vehicleInfo ||
                    (selectedVehicleGroupIds.length > 0 &&
                        !selectedVehicleGroupIds.includes(
                            vehicleInfo.vehicleGroupId
                        ))
                )
                    continue;

                const employee = shift.employeeId
                    ? employeeById.get(shift.employeeId.toLowerCase())
                    : undefined;

                const resolvedWtt = slot.workTimeTypeId
                    ? workTimeTypeById.get(slot.workTimeTypeId)
                    : undefined;

                mapped.push({
                    vehicleId: slot.vehicleId,
                    registerNumber: vehicleInfo.registerNumber ?? null,
                    brand: vehicleInfo.brand ?? null,
                    employeeId: shift.employeeId,
                    employeeName: employee?.fullName ?? null,
                    workTimeTypeName: resolvedWtt?.name ?? null,
                    workTimeTypeType: resolvedWtt?.type ?? null,
                    employeeWorkTimeTypes: [
                        {
                            employeeId: shift.employeeId,
                            employeeName: employee?.fullName ?? "",
                            workTimeTypeId: slot.workTimeTypeId,
                            workTimeTypeName: resolvedWtt?.name ?? "",
                            workTimeTypeType: resolvedWtt?.type ?? null,
                        },
                    ],
                    carId: null,
                    codriverGmt: null,
                    codriverId: null,
                    coords: { lat: 0, lng: 0 },
                    driverGmt: null,
                    driverId: null,
                    driving: undefined,
                    drivingGmt: null,
                    latitude: 0,
                    longitude: 0,
                    speed: 0,
                    timestamp: null,
                } as VehicleData);

                mappedVehicleIds.add(slot.vehicleId.toLowerCase());
            }
        }

        // Attach transport orders to vehicles
        if (transportOrders?.length) {
            const now = new Date();
            const ordersByVehicle = new Map<string, typeof transportOrders>();
            for (const order of transportOrders) {
                if (!order.vehicleId) continue;
                const start = order.scheduledStartDateTime
                    ? new Date(order.scheduledStartDateTime)
                    : null;
                const end = order.scheduledEndDateTime
                    ? new Date(order.scheduledEndDateTime)
                    : null;
                if (start && start > now) continue;
                if (end && end < now) continue;
                const vid = order.vehicleId.toLowerCase();
                if (!ordersByVehicle.has(vid)) {
                    ordersByVehicle.set(vid, []);
                }
                ordersByVehicle.get(vid).push(order);
            }
            for (const vehicle of mapped) {
                const vid = vehicle.vehicleId?.toLowerCase();
                if (vid && ordersByVehicle.has(vid)) {
                    vehicle.orders = ordersByVehicle.get(vid);
                }
            }
        }

        return mapped;
    }, [
        baseVehicleData,
        workShifts,
        selectedVehicleGroupIds,
        vehicleById,
        employeeById,
        transportOrders,
        workTimeTypeById,
    ]);

    // Primary filter counts from enriched vehicleData (before primary filtering)
    const countByPrimaryFilter = useMemo(() => {
        let working = 0;
        let notWorking = 0;

        vehicleData.forEach((v) => {
            if (hasActiveWorkTime(v)) {
                working++;
            } else {
                notWorking++;
            }
        });

        const vehicleEmployeeIds = new Set(
            vehicleData.map((v) => v.employeeId?.toLowerCase()).filter(Boolean)
        );
        let employeesWithoutVehiclesCount = 0;
        employees?.forEach((e) => {
            const eid = e.id?.toLowerCase();
            if (vehicleEmployeeIds.has(eid)) return;
            employeesWithoutVehiclesCount++;
            const hasActiveShift = eid
                ? workShiftsByEmployeeId.get(eid)?.some((w) => !w.endTime)
                : false;
            if (hasActiveShift) {
                working++;
            } else {
                notWorking++;
            }
        });

        return {
            all: vehicleData.length + employeesWithoutVehiclesCount,
            working,
            notWorking,
        };
    }, [vehicleData, employees, workShiftsByEmployeeId]);

    const filteredVehicleData = useMemo(() => {
        return vehicleData.filter((vehicle) => vehiclePrimaryFilter(vehicle));
    }, [vehicleData, vehiclePrimaryFilter]);

    const filteredEmployeeData: EmployeeData[] = useMemo(
        () =>
            employees
                ?.filter((e) => {
                    const eid = e.id?.toLowerCase();
                    const position = eid ? positionByEmployeeId.get(eid) : undefined;
                    return employeeFilter(position);
                })
                .map((e) => mapEmployeeData(e))
                .filter(
                    (p) =>
                        selectedVehicleGroupIds.length === 0 ||
                        selectedVehicleGroupIds.includes(
                            vehicleById.get(p.vehicleId?.toLowerCase())
                                ?.vehicleGroupId
                        )
                )
                .sort((a, b) => {
                    if (sortOrder === "employeeName") {
                        return Base.stringCompare(
                            a.employeeName ?? "",
                            b.employeeName ?? ""
                        );
                    }
                    return Base.stringCompare(
                        a.registerNumber,
                        b.registerNumber
                    );
                }) ?? [],
        [
            positionByEmployeeId,
            mapEmployeeData,
            employeeFilter,
            employees,
            selectedVehicleGroupIds,
            sortOrder,
            vehicleById,
        ]
    );

    const fleetSummaryTotals: MonitorViewFleetSummaryTotals = useMemo(() => {
        return {
            activeVehicles: filteredVehicleData.length,
            totalVehicles: positions?.length || 0,
            activeEmployees: filteredEmployeeData.length,
            totalEmployees: employees?.length || 0,
        };
    }, [filteredVehicleData, filteredEmployeeData, employees, positions]);

    return {
        filteredVehicleData,
        filteredEmployeeData,
        countByPrimaryFilter,
        fleetSummaryTotals,
        countByWorkTimeType,
        countByDrivingStatus,
        positionByEmployeeId,
    };
}
